import axios from 'axios';
import { useState } from 'react';

import type { ImageUrls } from '../types/news';
import {
  JobGroup,
  JobLevel,
  JobTitle,
  JobTitlesByGroup,
  type TeamRole,
} from '../types/team';
import { getFileExtension, uploadImage } from '../utils/imageUpload';

export { JobGroup, JobLevel, JobTitle, JobTitlesByGroup, type TeamRole };

/**
 * Constructs a team member object ready for persistence.
 */
function buildTeamMember(fields: {
  name: string;
  role: TeamRole | null;
  imageUrls: ImageUrls | null;
  expirationDate?: string;
}) {
  const { name, role, imageUrls, expirationDate } = fields;

  return {
    id: crypto.randomUUID(),
    name,
    role,
    image: imageUrls,
    expirationDate,
    createdAt: new Date().toISOString(),
  };
}

/**
 * Hook managing the state and actions for the team member creation form.
 */
export function useTeamEditor() {
  const [name, setName] = useState('');
  const [role, setRole] = useState<TeamRole | null>(null);
  const [expirationDate, setExpirationDate] = useState('');

  const [imageUrls, setImageUrls] = useState<ImageUrls | null>(null);
  const [imageUploading, setImageUploading] = useState(false);

  const [lastSavedId, setLastSavedId] = useState<string | null>(null);
  const [publishState, setPublishState] = useState<{
    status: 'idle' | 'loading' | 'success' | 'error';
    message?: string;
  }>({ status: 'idle' });

  const today = new Date().toISOString().split('T')[0];
  const isValid =
    name.trim() !== '' &&
    role !== null &&
    imageUrls !== null &&
    expirationDate >= today;

  async function handleImageUpload(file: File): Promise<void> {
    setImageUploading(true);

    try {
      const filename = `team/${crypto.randomUUID()}.${getFileExtension(
        file.name,
      )}`;

      const urls = await uploadImage(filename, file);

      setImageUrls(urls);
    } catch (err) {
      console.error('Bild-Upload fehlgeschlagen', err);
    } finally {
      setImageUploading(false);
    }
  }

  async function handlePublish(): Promise<void> {
    setPublishState({ status: 'loading' });

    try {
      const member = buildTeamMember({
        name,
        role,
        imageUrls,
        expirationDate,
      });

      const { data } = await axios.post<{
        uploadUrl: string;
      }>('http://localhost:3001/upload', {
        filename: `team/${member.id}.json`,
        contentType: 'application/json',
      });

      await axios.put(data.uploadUrl, JSON.stringify(member), {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setLastSavedId(member.id);
      setPublishState({ status: 'success' });
    } catch (err) {
      setPublishState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Unbekannter Fehler',
      });
    }
  }

  return {
    name,
    setName,

    role,
    setRole,

    expirationDate,
    setExpirationDate,

    imageUrls,
    imageUploading,

    publishState,
    isValid,
    lastSavedId,

    handleImageUpload,
    handlePublish,
  };
}
